import React from "react";
import { Button, Collapse } from "antd";
import type { CollapseProps } from "antd";
import { useNavigate } from "react-router";
import JumpToIcon from "../../assets/icons/jump_to_element.svg?react";
import CheckboxIcon from "../../assets/icons/checkbox.svg?react";
import {
  groupNotificationsByDateAndType,
  getTime,
} from "../../utils/dateUtils";
import { useNotifications } from "../../hooks/useNotifications";
import StatusTransition from "../UI/StatusTransition";
import SentenceCreator from "./SentenceCreator";
import type { Notification } from "./notifications";
import { useScopeParams } from "../../hooks/useScopeParams";

const headerStyle: React.CSSProperties = {
  border: "none",
  paddingBottom: 0,
};

const bodyStyle: React.CSSProperties = {
  paddingTop: 0,
  paddingBottom: 0,
};

type NotificationsPanelBodyProps = {
  notifications: Notification[];
  trialId: string;
};

const NotificationsPanelBody = ({
  notifications,
  trialId,
}: NotificationsPanelBodyProps) => {
  const navigate = useNavigate();
  const { buildUrl } = useScopeParams();
  const { markAsRead, markAsReadLoading } = useNotifications(trialId);

  const groupedNotifications = groupNotificationsByDateAndType(
    notifications as Array<{
      notification_data?: { at?: string | number | Date };
      type: string;
    }>,
  );

  const handleMarkRead = (notificationId: string) => {
    markAsRead(notificationId);
  };

  const notificationItems: CollapseProps["items"] = Object.entries(
    groupedNotifications,
  ).map(([date, types]) => ({
    key: date,
    label: (
      <div className="flex items-center gap-2">
        <p className="text-[#00000073] text-sm font-semibold">{date}</p>
      </div>
    ),
    children: Object.entries(types).map(([, notifs]) => {
      const notifications = notifs as Notification[];
      return (
        <div key={`${date}-panel`} className="flex flex-col mb-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="flex flex-col border-b border-[#EFF1F2] hover:bg-[#00000005] relative group"
            >
              <div className="flex items-center justify-between px-0 mt-2 mb-0">
                <div className="flex items-center gap-2">
                  <p className="text-[#00000073] text-sm">
                    {getTime(notification.notification_data?.at ?? "")}
                  </p>
                </div>
                {!notification.is_read && (
                  <>
                    <span className="h-[10px] w-[10px] bg-[#0C98D0] rounded-full group-hover:opacity-0 transition-opacity duration-200" />
                    <div className="absolute right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Button
                        type="default"
                        onClick={() => handleMarkRead(notification.id)}
                        className="text-[#556C75] text-xs hover:text-blue-400 px-3 py-1 h-[24px] border-0 shadow-none"
                        icon={
                          <CheckboxIcon className="text-[#556C75] w-[14px] h-[14px]" />
                        }
                        loading={markAsReadLoading}
                      >
                        Mark read
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex px-0 py-2">
                <SentenceCreator
                  notification={notification}
                  type={notification?.notification_data?.field as any}
                />
              </div>
              {notification.notification_data?.field && (
                <div className="flex gap-2 px-0 pb-4">
                  <StatusTransition
                    type={notification.notification_data.field as any}
                    fromValue={notification.notification_data.from_value}
                    toValue={notification.notification_data.to_value}
                    avatarConfig={{
                      avatarSize: 24,
                      avatarFontSize: 10,
                      labelFontSize: 14,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }),
    styles: { header: headerStyle, body: bodyStyle },
  }));

  const keyList = notificationItems?.map((item) => item?.key as string) || [];

  return (
    <div className="flex flex-col h-[600px] w-[350px] overflow-auto notifications">
      <div className="notifications-body flex-1 overflow-y-auto">
        <Collapse
          className="notifications"
          items={notificationItems}
          ghost
          expandIconPlacement="end"
          defaultActiveKey={keyList}
        />
      </div>
      <div className="flex justify-center px-4 py-0">
        <Button
          type="default"
          className="text-[#2B4752] mt-4 px-5 py-4 h-[40px] gap-[10px] flex items-center justify-center border border-[#2B4752]"
          size="small"
          icon={<JumpToIcon />}
          onClick={() => navigate(buildUrl("notifications"))}
          data-testid="view-all-notifications-button"
        >
          <p className="text-sm">View all notifications</p>
        </Button>
      </div>
    </div>
  );
};

export default NotificationsPanelBody;
